import { PayloadAction } from "@reduxjs/toolkit";
import { FormError, userOnboardingStage } from "app/types";
import { createSlice } from "utils/@reduxjs/toolkit";
import { IInviteVerification, PLAN_TYPE, TeamInviteAction } from "../types";
import {
  defaultDefaultCustomization,
  defaultFormElement,
  defaultTeamForm,
  initialState,
} from "./initialState";

const formSlice = createSlice({
  name: "OnBoardingState",
  initialState,
  reducers: {
    nextStep: (state) => {
      state.teamFormActiveIndex = Math.min(state.teamFormActiveIndex + 1, 4);
    },
    previousStep: (state) => {
      state.teamFormActiveIndex = Math.max(state.teamFormActiveIndex - 1, 1);
    },
    setTeamFormActiveIndex: (state, action: PayloadAction<number>) => {
      state.teamFormActiveIndex = action.payload;
    },

    setProfileFormValue: (
      state,
      action: PayloadAction<{ name: string; value: string }>
    ) => {
      state.profileForm[action.payload.name].value = action.payload.value;
      state.profileForm[action.payload.name].error = "";
    },
    setProfileFormErrors: (state, action: PayloadAction<FormError[]>) => {
      action.payload.forEach((error) => {
        state.profileForm[error.name].error = error.error;
      });
    },
    saveProfileRequest: (
      state,
      action: PayloadAction<{  inviteToken?: string; callback: (onBoardingState:userOnboardingStage) => void }>
    ) => {
      state.profileForm.isLoading = true;
    },
    clearProfileForm: (state) => {
      state.profileForm = initialState.profileForm;
    },
    saveProfileRequestComplete: (state) => {
      state.profileForm.isLoading = false;
    },
    setSetupFormValue: (
      state,
      action: PayloadAction<{ name: string; value: string | string[] }>
    ) => {
      state.setupForm[action.payload.name].value = action.payload.value;
      state.setupForm[action.payload.name].error = "";
    },
    saveSetupRequest: (
      state,
      action: PayloadAction<{
        isFrom: "teamCreate" | "isFinish";
        callback?: () => void;
      }>
    ) => {
      const isFrom = action.payload.isFrom;
      if (isFrom === "teamCreate") {
        state.setupForm.isTeamCreated = true;
      } else {
        state.setupForm.isLoading = true;
      }
    },
    clearSetupForm: (state) => {state.setupForm = initialState.setupForm },
    
    saveSetupRequestComplete: (state) => {
      state.setupForm.isLoading = false;
    },
    getOnbordingList(state) {
      state.isLoading = true;
    },
    setOnboradingList: (state, action: PayloadAction<any>) => {
      state.onboardingList.companySizes = action.payload?.data?.companySizes;
      state.onboardingList.referalSources =
        action.payload?.data?.referralSources;
      state.onboardingList.toolPreferences =
        action.payload?.data?.toolPreferences;
      state.isLoading = false;
    },
    resetStore: () => initialState,

    setTeamFormValue: (
      state,
      action: PayloadAction<{ name: string; value: string }>
    ) => {
      state.teamForm[action.payload.name].value = action.payload.value;
      state.teamForm[action.payload.name].error = "";
    },
    setTeamPlan: (state, action: PayloadAction<{ value: PLAN_TYPE }>) => {
      state.teamForm.plan = action.payload.value;
    },
    setTeamFormErrors: (state, action: PayloadAction<FormError[]>) => {
      action.payload.forEach((error) => {
        state.teamForm[error.name].error = error.error;
      });
    },
    setTeamProjectName: (state, action: PayloadAction<{ value: string }>) => {
      state.teamForm.project.name.value = action.payload.value;
      state.teamForm.project.name.error = "";
    },
    setTeamProjectDefaultCustomization: (
      state,
      action: PayloadAction<{ name: string; value: boolean }>
    ) => {
      state.teamForm.project.defaultCustomization[action.payload.name] =
        action.payload.value;
    },
    setTeamProjectNameError: (
      state,
      action: PayloadAction<{ error: string }>
    ) => {
      state.teamForm.project.name.error = action.payload.error;
    },
    addTeamMember: (state) => {
      state.teamForm.members = [
        ...state.teamForm.members,
        {
          name: {
            value: "",
            error: "",
          },
          role: {
            value: "",
            error: "",
          },
        },
      ];
    },
    setTeamCoupon: (state, action: PayloadAction<{ value: string }>) => {
      state.teamForm.coupon.value = action.payload.value;
      state.teamForm.coupon.error = "";
    },
    removeTeamMember: (state, action: PayloadAction<{ index: number }>) => {
      state.teamForm.members = state.teamForm.members.filter(
        (_, index) => index !== action.payload.index
      );
    },
    clearTeamMember: (state, action: PayloadAction<{ index: number }>) => {
      const idx = action.payload.index;
      state.teamForm.members[idx] = {
        name: { value: "", error: "" },
        role: { value: "", error: "" },
      };
    },
    updateTeamMember: (
      state,
      action: PayloadAction<{ index: number; name: string; value: string }>
    ) => {
      state.teamForm.members[action.payload.index][action.payload.name].value =
        action.payload.value;
      state.teamForm.members[action.payload.index][action.payload.name].error =
        "";
    },
    updateTeamMemberError: (
      state,
      action: PayloadAction<{ index: number; name: string; error: string }>
    ) => {
      state.teamForm.members[action.payload.index][action.payload.name].error =
        action.payload.error;
    },
    saveTeamRequest: (
      state,
      action: PayloadAction<{ callback?: () => void }>
    ) => {
      state.teamForm.isLoading = true;
    },
    createTeamProject: (
      state,
      action: PayloadAction<{ callback: () => void }>
    ) => {},
    saveTeamRequestComplete: (state) => {
      state.teamForm.isLoading = false;
    },
    setTeamLoadingState: (state, action: PayloadAction<boolean>) => {
      state.teamForm.isProjectCreated = action.payload;
    },
    setIsMemberAdded: (state, action: PayloadAction<boolean>) => {
      state.teamForm.isMemberAdded = action.payload;
    },
    createProjectMember: (
      state,
      action: PayloadAction<{ callback: () => void }>
    ) => {},
    updateTeamFormValue: (
      state,
      action: PayloadAction<{ name: string; value: any }>
    ) => {
      state.teamForm[action.payload.name] = action.payload.value;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsButtonLoading: (state, action: PayloadAction<boolean>) => {
      state.isButtonLoading = action.payload;
    },
    emailInviteVerification: (
      state,
      action: PayloadAction<{ token: string; callback: (isUserExist:boolean) => void }>
    ) => {
      state.isLoading = true;
    },
    emailTeamInvitationStatus: (
      state,
      action: PayloadAction<{ status: TeamInviteAction; token: string; callback: () => void }>
    ) => {
      state.isButtonLoading = true;
    },
    setInviteVerification: (
      state,
      action: PayloadAction<IInviteVerification>
    ) => {
      state.inviteVerification = action.payload;
    },
    clearTeamForm: (state) => {
      state.teamForm = { ...defaultTeamForm };
    },
    clearTeamProjectForm: (state) => {
      state.teamForm.name = { ...defaultFormElement };
      state.teamForm.project.name = { ...defaultFormElement };
      state.teamForm.project.defaultCustomization = {
        ...defaultDefaultCustomization,
      };
    },
  },
});

export const {
  actions,
  reducer: onBoardingReducer,
  name: onBoardingSlice,
} = formSlice;
